/**
 * Hook: Admin-Inbox (Liste aller Konversationen + Live-Updates)
 *
 * Verantwortlich für:
 *   - Initial: REST-Load aller aktiven Konversationen
 *   - Live: WS 'message:new' → Liste an den Anfang verschieben + unread+1
 *           WS 'message:read' (von User) → ggf. Anzeige aktualisieren
 *
 * Wichtig: Hook ist KEIN Singleton. Mehrere Konsumenten könnten parallel die
 * Liste rendern (z.B. Admin-Sidebar mit Mini-Preview + Volle Inbox-Seite),
 * jeder hat seinen eigenen State. Das ist okay weil der REST-Call gecached
 * werden kann (Browser-Cache 5s) und die WS-Subscription leichtgewichtig ist.
 */

import { useCallback, useEffect, useState } from 'react';
import {
  fetchAdminConversations,
  type AdminConversationListItem
} from '../services/support/adminChatService';
import { useChatWebSocket } from './useChatWebSocket';

interface UseAdminChatInboxOptions {
  enabled?: boolean;
}

interface UseAdminChatInboxResult {
  conversations: AdminConversationListItem[];
  isLoading: boolean;
  error: Error | null;
  refresh: () => Promise<void>;
}

export function useAdminChatInbox(options: UseAdminChatInboxOptions = {}): UseAdminChatInboxResult {
  const enabled = options.enabled !== false;
  const [conversations, setConversations] = useState<AdminConversationListItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const { subscribe } = useChatWebSocket({ enabled });

  const refresh = useCallback(async (): Promise<void> => {
    if (!enabled) return;
    try {
      const list = await fetchAdminConversations();
      setConversations(list);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Inbox konnte nicht geladen werden'));
    } finally {
      setIsLoading(false);
    }
  }, [enabled]);

  // Initial-Load
  useEffect(() => {
    if (!enabled) {
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    void refresh();
  }, [enabled, refresh]);

  // Live: Admin hat Konversation gelesen → unread-Counter in der Liste auf 0.
  // Sonst zeigt die Inbox-Liste auch nach Rückkehr aus dem Thread weiterhin
  // die alte Badge-Zahl (Bug: User sah nach Auto-Mark-Read im Thread immer
  // noch "2 ungelesen" in der Konversations-Liste).
  useEffect(() => {
    if (!enabled) return;
    return subscribe('message:read', (event) => {
      if (event.readByType !== 'admin') return;
      setConversations((prev) => {
        const idx = prev.findIndex((c) => c.id === event.conversationId);
        if (idx === -1) return prev;
        if (prev[idx]!.unreadCountAdmin === 0) return prev;
        const updated: AdminConversationListItem = {
          ...prev[idx]!,
          unreadCountAdmin: 0
        };
        const next = [...prev];
        next[idx] = updated;
        return next;
      });
    });
  }, [enabled, subscribe]);

  // Live: neue User-Nachricht → Konversation an Anfang + unread++
  useEffect(() => {
    if (!enabled) return;
    return subscribe('message:new', (event) => {
      // Wenn die Nachricht vom Admin selbst stammt (mehrere Tabs), nicht hochzaehlen
      if (event.message.senderType !== 'user') {
        // Trotzdem: Liste neu sortieren + Preview updaten falls Admin antwortet
        setConversations((prev) => {
          const idx = prev.findIndex((c) => c.id === event.conversationId);
          if (idx === -1) return prev;
          const updated: AdminConversationListItem = {
            ...prev[idx]!,
            lastMessageAt: event.message.createdAt,
            lastMessagePreview: event.message.content.slice(0, 80),
            lastMessageSenderType: 'admin'
          };
          const without = prev.filter((_, i) => i !== idx);
          return [updated, ...without];
        });
        return;
      }

      setConversations((prev) => {
        const idx = prev.findIndex((c) => c.id === event.conversationId);
        if (idx === -1) {
          // Neue Konversation, die wir noch nicht in der Liste haben → Refresh fetchen
          // (passiert wenn ein User zum ersten Mal überhaupt schreibt)
          void refresh();
          return prev;
        }
        const updated: AdminConversationListItem = {
          ...prev[idx]!,
          unreadCountAdmin: prev[idx]!.unreadCountAdmin + 1,
          lastMessageAt: event.message.createdAt,
          lastMessagePreview: event.message.content.slice(0, 80),
          lastMessageSenderType: 'user'
        };
        const without = prev.filter((_, i) => i !== idx);
        return [updated, ...without];
      });
    });
  }, [enabled, subscribe, refresh]);

  return { conversations, isLoading, error, refresh };
}
